import * as React from 'react';
import { toast } from 'sonner';
import { copyToClipboard, openUrlInNewTab } from '../../../../../../models/UiUtils';
import { useClientTrackSatisfactionResponseClickThroughMutation } from '../../../../../../queries';
import { ISatisfactionSurvey, ISatisfactionSurveyResponse } from '../../../../../models/Api';

export const useHandleSatisfactionSurveyResponse = ({
	response,
	survey,
	token,
}: {
	response?: ISatisfactionSurveyResponse;
	survey?: ISatisfactionSurvey;
	token?: string;
}) => {
	const responseRef = React.useRef<ISatisfactionSurveyResponse | undefined>(response);
	const [isLoading, setIsLoading] = React.useState(false);

	// Update the ref when response changes
	React.useEffect(() => {
		responseRef.current = response;
	}, [response]);

	// Ensure ref is always up to date
	responseRef.current = response;
	const { mutateAsync: trackSatisfactionSurveyResponseClickThroughAsync } =
		useClientTrackSatisfactionResponseClickThroughMutation({});

	const [reviewGiven, setReviewGiven] = React.useState(false);

	const requestReview = React.useMemo(
		() =>
			Boolean(
				survey?.reviewRequestSettings?.isEnabled &&
					survey?.reviewRequestSettings.ratings?.indexOf(response?.rating) >= 0 &&
					!reviewGiven
			),
		[survey?.reviewRequestSettings?.isEnabled, survey?.reviewRequestSettings.ratings, response?.rating, reviewGiven]
	);

	const onCopyFeedbackToClipboardClicked = React.useCallback(async () => {
		const satisfactionSurveyResponse: ISatisfactionSurveyResponse = response;
		const textToCopy = satisfactionSurveyResponse?.feedback || '';
		if (await copyToClipboard(textToCopy)) {
			toast.success("We've copied your review to your clipboard");
			return;
		}
		toast.error('Error copying feedback to clipboard.');
	}, [response]);

	const onReviewLinkClicked = React.useCallback(
		async (e: React.MouseEvent<HTMLElement>) => {
			const satisfactionSurveyResponse: ISatisfactionSurveyResponse = response;

			if (response && token && survey?.reviewRequestSettings?.reviewUrl) {
				e.preventDefault();

				// Open URL IMMEDIATELY using anchor element click (more reliable than window.open for Safari)
				// Safari's popup blocker is more lenient with programmatic anchor clicks vs window.open()
				// This must be called synchronously within the user event handler (before any await)
				// Using openUrlInNewTab which creates an anchor element and clicks it programmatically
				openUrlInNewTab(survey.reviewRequestSettings.reviewUrl);

				setIsLoading(true);
				try {
					// Copy feedback to clipboard if present (async operation happens after window is opened)
					if (satisfactionSurveyResponse?.feedback?.length > 0) {
						await onCopyFeedbackToClipboardClicked();
					}

					// Track the click-through (async operation happens after window is opened)
					await trackSatisfactionSurveyResponseClickThroughAsync({
						accessToken: token,
						response,
					});
				} catch (error) {
					console.error('Error tracking review click through:', error);
					// Note: We don't close the window if API call fails because:
					// 1. User is already on Google (which is the main goal)
					// 2. Closing a window that user is viewing is poor UX
					// 3. Safari may not allow closing a window that was opened synchronously
				} finally {
					setReviewGiven(true);
					setIsLoading(false);
				}
			}
		},
		[
			onCopyFeedbackToClipboardClicked,
			response,
			token,
			trackSatisfactionSurveyResponseClickThroughAsync,
			survey?.reviewRequestSettings?.reviewUrl,
		]
	);

	return {
		trackSatisfactionSurveyResponseClickThroughAsync,
		requestReview,
		onCopyFeedbackToClipboardClicked,
		onReviewLinkClicked,
		isLoading,
	} as const;
};
